package com.arenaproplay.admin.pages

import androidx.compose.runtime.*
import com.arenaproplay.admin.di.LocalAppContainer
import com.arenaproplay.admin.domain.admin.AppAnnouncementRow
import com.arenaproplay.admin.domain.admin.AppUpdatePolicyMap
import com.arenaproplay.admin.domain.admin.AppUpdatePolicyRow
import com.arenaproplay.admin.domain.admin.SaveAppAnnouncementPayload
import com.arenaproplay.admin.ui.components.admin.*
import com.arenaproplay.admin.ui.layouts.AdminLayout
import com.arenaproplay.admin.ui.theme.AppColors
import com.arenaproplay.admin.ui.theme.bodyMMedium
import com.arenaproplay.admin.ui.theme.bodyMRegular
import com.arenaproplay.admin.ui.theme.bodySMedium
import com.arenaproplay.admin.ui.theme.toRgbaString
import com.arenaproplay.admin.ui.utils.formatTimestampShort
import com.arenaproplay.admin.ui.utils.pickImageFile
import com.arenaproplay.admin.ui.utils.sBorderRadius
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.TextArea

// Content languages the apps ship — the web codes (Ukrainian = "ua").
private val CONTENT_LANGS = listOf("en", "ua", "de", "es", "fr", "it", "pl", "pt")
private val PLATFORMS = listOf("android", "ios")

// Dark-theme form palette (mirrors SearchInput / GlassCard, NOT the light theme).
private const val INPUT_BG = "#0B1220"
private const val INPUT_BORDER = "1px solid rgba(255,255,255,0.10)"
private const val BLOCK_BG = "#0F1626"
private const val BLOCK_BORDER = "1px solid rgba(255,255,255,0.06)"

/**
 * Launch popup management: the per-platform version gate (force/soft update)
 * and the "what's new" announcements shown once per user on app start.
 */
@Page("/app-launch")
@Composable
fun AppLaunchPage() {
    val repo = LocalAppContainer.current.adminRepository ?: return
    val scope = rememberCoroutineScope()
    val loadState = rememberLoadState()

    var policy by remember { mutableStateOf<AppUpdatePolicyMap?>(null) }
    var announcements by remember { mutableStateOf<List<AppAnnouncementRow>?>(null) }
    // null = list mode; non-null = the editor is open (empty id = create).
    var editing by remember { mutableStateOf<AppAnnouncementRow?>(null) }
    var busy by remember { mutableStateOf(false) }
    var actionError by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(loadState.reloadToken) {
        loadState.load {
            policy = repo.getAppUpdatePolicy()
            announcements = repo.getAppAnnouncements()
        }
    }

    AdminLayout(
        title = "App Launch",
        subtitle = "Version gate and what's-new popups shown on app start",
    ) {
        if (loadState.error != null) {
            ErrorCard(loadState)
            return@AdminLayout
        }

        // ── Version gate ──
        policy?.let { current ->
            UpdatePolicyCard(
                policy = current,
                busy = busy,
                onSave = { updated ->
                    busy = true
                    actionError = null
                    scope.launch {
                        try {
                            repo.setAppUpdatePolicy(updated)
                            policy = updated
                        } catch (e: Throwable) {
                            actionError = e.message
                        } finally {
                            busy = false
                        }
                    }
                },
            )
        }

        VSpace(16)

        // ── Announcements ──
        AdminCard {
            Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                SpanText("Announcements", Modifier.bodyMMedium().color(AppColors.Ink))
                Box(Modifier.weight(1f))
                ActionChip("+ New announcement", enabled = editing == null) {
                    editing = AppAnnouncementRow(
                        id = "",
                        active = true,
                        platforms = PLATFORMS,
                        title = emptyMap(),
                        body = emptyMap(),
                        createdAt = "",
                    )
                }
            }
            VSpace(12)

            val items = announcements
            when {
                items == null -> SpanText("Loading…", Modifier.bodyMRegular().color(AppColors.Muted))
                items.isEmpty() && editing == null ->
                    SpanText("No announcements yet", Modifier.bodyMRegular().color(AppColors.Muted))
                else -> items.forEach { row ->
                    AnnouncementListRow(
                        row = row,
                        onEdit = { editing = row },
                        onDelete = {
                            busy = true
                            scope.launch {
                                try {
                                    repo.deleteAppAnnouncement(row.id)
                                    announcements = announcements?.filter { it.id != row.id }
                                } catch (e: Throwable) {
                                    actionError = e.message
                                } finally {
                                    busy = false
                                }
                            }
                        },
                    )
                    VSpace(8)
                }
            }

            editing?.let { current ->
                VSpace(16)
                AnnouncementEditor(
                    initial = current,
                    busy = busy,
                    onCancel = { editing = null },
                    onSave = { payload ->
                        busy = true
                        actionError = null
                        scope.launch {
                            try {
                                val saved =
                                    if (current.id.isEmpty()) {
                                        repo.createAppAnnouncement(payload)
                                    } else {
                                        repo.updateAppAnnouncement(current.id, payload)
                                    }
                                announcements =
                                    if (current.id.isEmpty()) {
                                        listOf(saved) + announcements.orEmpty()
                                    } else {
                                        announcements?.map { if (it.id == saved.id) saved else it }
                                    }
                                editing = null
                            } catch (e: Throwable) {
                                actionError = e.message
                            } finally {
                                busy = false
                            }
                        }
                    },
                )
            }

            actionError?.let {
                VSpace(8)
                SpanText(it, Modifier.bodySMedium().color(AppColors.Error))
            }
        }
        VSpace(28)
    }
}

// ── Version gate card ────────────────────────────────────────────────────

@Composable
private fun UpdatePolicyCard(
    policy: AppUpdatePolicyMap,
    busy: Boolean,
    onSave: (AppUpdatePolicyMap) -> Unit,
) {
    // Editable copies, seeded from the loaded policy.
    val mins = remember(policy) {
        mutableStateMapOf<String, String>().apply {
            PLATFORMS.forEach { put(it, policy.platforms[it]?.minSupportedVersion?.toString() ?: "") }
        }
    }
    val latests = remember(policy) {
        mutableStateMapOf<String, String>().apply {
            PLATFORMS.forEach { put(it, policy.platforms[it]?.latestVersion?.toString() ?: "") }
        }
    }
    val stores = remember(policy) {
        mutableStateMapOf<String, String>().apply {
            PLATFORMS.forEach { put(it, policy.platforms[it]?.storeUrl ?: "") }
        }
    }

    AdminCard {
        SpanText("Update policy", Modifier.bodyMMedium().color(AppColors.Ink))
        VSpace(4)
        SpanText(
            "Builds below \"min supported\" are force-blocked; builds below \"latest\" see a dismissible update popup.",
            Modifier.bodySMedium().color(AppColors.Muted),
        )
        VSpace(14)
        PLATFORMS.forEach { platform ->
            Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.Bottom) {
                Box(Modifier.width(70.px).padding(bottom = 10.px)) {
                    SpanText(platform.replaceFirstChar { it.uppercase() }, Modifier.bodyMMedium().color(AppColors.Ink))
                }
                LabeledField("Min supported (build)", mins[platform] ?: "", width = 160) { mins[platform] = it }
                Box(Modifier.width(10.px))
                LabeledField("Latest (build)", latests[platform] ?: "", width = 160) { latests[platform] = it }
                Box(Modifier.width(10.px))
                LabeledField("Store URL", stores[platform] ?: "", width = 380) { stores[platform] = it }
            }
            VSpace(10)
        }
        VSpace(4)
        ActionChip(if (busy) "Saving…" else "Save policy", enabled = !busy) {
            val platforms = buildMap {
                PLATFORMS.forEach { p ->
                    val min = mins[p]?.toIntOrNull()
                    val latest = latests[p]?.toIntOrNull()
                    val store = stores[p]?.trim().orEmpty()
                    if (min != null && latest != null && store.isNotEmpty()) {
                        put(p, AppUpdatePolicyRow(min, latest, store))
                    }
                }
            }
            onSave(AppUpdatePolicyMap(platforms))
        }
    }
}

// ── Announcement list + editor ───────────────────────────────────────────

@Composable
private fun AnnouncementListRow(
    row: AppAnnouncementRow,
    onEdit: () -> Unit,
    onDelete: () -> Unit,
) {
    Row(
        Modifier
            .fillMaxWidth()
            .sBorderRadius(12)
            .padding(leftRight = 14.px, topBottom = 10.px)
            .styleModifier {
                property("background", BLOCK_BG)
                property("border", BLOCK_BORDER)
            },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        // Thumbnail — the news look at a glance.
        row.imageUrl?.let { url ->
            Div(
                attrs = Modifier
                    .sBorderRadius(8)
                    .styleModifier {
                        property("width", "56px")
                        property("height", "36px")
                        property("overflow", "hidden")
                        property("flex-shrink", "0")
                        property("background", INPUT_BG)
                    }
                    .toAttrs()
            ) {
                Img(
                    src = url,
                    attrs = {
                        style {
                            property("width", "100%")
                            property("height", "100%")
                            property("object-fit", "cover")
                        }
                    }
                )
            }
            Box(Modifier.width(12.px))
        }
        Column(Modifier.weight(1f)) {
            SpanText(row.title["en"] ?: "(untitled)", Modifier.bodyMMedium().color(AppColors.Ink))
            SpanText(
                "${row.platforms.joinToString("+")} · ${formatTimestampShort(row.createdAt)}",
                Modifier.bodySMedium().color(AppColors.Muted),
            )
        }
        Pill(if (row.active) "Active" else "Off", if (row.active) PillTone.Accent else PillTone.Neutral)
        Box(Modifier.width(10.px))
        ActionChip("Edit", enabled = true, onClick = onEdit)
        Box(Modifier.width(8.px))
        ActionChip("Delete", enabled = true, danger = true, onClick = onDelete)
    }
}

@Composable
private fun AnnouncementEditor(
    initial: AppAnnouncementRow,
    busy: Boolean,
    onCancel: () -> Unit,
    onSave: (SaveAppAnnouncementPayload) -> Unit,
) {
    val repo = LocalAppContainer.current.adminRepository
    val scope = rememberCoroutineScope()

    var active by remember { mutableStateOf(initial.active) }
    val platforms = remember { mutableStateMapOf<String, Boolean>().apply { PLATFORMS.forEach { put(it, it in initial.platforms) } } }
    val titles = remember { mutableStateMapOf<String, String>().apply { CONTENT_LANGS.forEach { put(it, initial.title[it] ?: "") } } }
    val bodies = remember { mutableStateMapOf<String, String>().apply { CONTENT_LANGS.forEach { put(it, initial.body[it] ?: "") } } }
    var imageUrl by remember { mutableStateOf(initial.imageUrl ?: "") }
    var ctaUrl by remember { mutableStateOf(initial.ctaUrl ?: "") }
    var selectedLang by remember { mutableStateOf("en") }
    var uploading by remember { mutableStateOf(false) }
    var uploadError by remember { mutableStateOf<String?>(null) }

    Column(
        Modifier
            .fillMaxWidth()
            .sBorderRadius(12)
            .padding(16.px)
            .styleModifier {
                property("background", BLOCK_BG)
                property("border", BLOCK_BORDER)
            },
    ) {
        SpanText(
            if (initial.id.isEmpty()) "New announcement" else "Edit announcement",
            Modifier.bodyMMedium().color(AppColors.Ink),
        )
        VSpace(12)

        Row(verticalAlignment = Alignment.CenterVertically) {
            ToggleChip("Active", active) { active = it }
            Box(Modifier.width(12.px))
            PLATFORMS.forEach { p ->
                ToggleChip(p.replaceFirstChar { it.uppercase() }, platforms[p] == true) { platforms[p] = it }
                Box(Modifier.width(8.px))
            }
        }
        VSpace(16)

        // ── Image: upload + 16:9 preview (the popup crops to 16:9 too) ──
        FieldLabel("Image (shown in the popup, cropped to 16:9)")
        VSpace(6)
        if (imageUrl.isNotBlank()) {
            Div(
                attrs = Modifier
                    .sBorderRadius(12)
                    .styleModifier {
                        property("width", "320px")
                        property("aspect-ratio", "16 / 9")
                        property("overflow", "hidden")
                        property("background", INPUT_BG)
                        property("border", BLOCK_BORDER)
                    }
                    .toAttrs()
            ) {
                Img(
                    src = imageUrl,
                    attrs = {
                        style {
                            property("width", "100%")
                            property("height", "100%")
                            property("object-fit", "cover")
                        }
                    }
                )
            }
            VSpace(8)
        }
        Row(verticalAlignment = Alignment.CenterVertically) {
            ActionChip(
                when {
                    uploading -> "Uploading…"
                    imageUrl.isBlank() -> "Upload image"
                    else -> "Replace image"
                },
                enabled = !uploading && repo != null,
            ) {
                pickImageFile { bytes, fileName, contentType ->
                    uploading = true
                    uploadError = null
                    scope.launch {
                        try {
                            imageUrl = repo!!.uploadAppLaunchImage(bytes, fileName, contentType)
                        } catch (e: Throwable) {
                            uploadError = e.message ?: "Upload failed"
                        } finally {
                            uploading = false
                        }
                    }
                }
            }
            if (imageUrl.isNotBlank()) {
                Box(Modifier.width(8.px))
                ActionChip("Remove image", enabled = !uploading, danger = true) { imageUrl = "" }
            }
        }
        uploadError?.let {
            VSpace(6)
            SpanText(it, Modifier.bodySMedium().color(AppColors.Error))
        }
        VSpace(16)

        LabeledField("CTA URL — optional, adds an \"Open\" button", ctaUrl, width = 500) { ctaUrl = it }
        VSpace(16)

        // ── Texts: one language at a time, tabs show what's filled ──
        FieldLabel("Texts — English is required, other languages fall back to it")
        VSpace(8)
        Row {
            CONTENT_LANGS.forEach { lang ->
                val filled = !titles[lang].isNullOrBlank() || !bodies[lang].isNullOrBlank()
                LangTab(
                    label = lang.uppercase() + if (filled) " •" else "",
                    selected = lang == selectedLang,
                    required = lang == "en",
                ) { selectedLang = lang }
                Box(Modifier.width(6.px))
            }
        }
        VSpace(10)
        LabeledField(
            "Title (${selectedLang.uppercase()})" + if (selectedLang == "en") " — required" else "",
            titles[selectedLang] ?: "",
            width = 690,
        ) { titles[selectedLang] = it }
        VSpace(8)
        FieldLabel("Body (${selectedLang.uppercase()})" + if (selectedLang == "en") " — required" else "")
        VSpace(4)
        BodyTextArea(bodies[selectedLang] ?: "") { bodies[selectedLang] = it }
        VSpace(16)

        Row {
            ActionChip(if (busy) "Saving…" else "Save", enabled = !busy) {
                onSave(
                    SaveAppAnnouncementPayload(
                        active = active,
                        platforms = PLATFORMS.filter { platforms[it] == true },
                        title = titles.filterValues { it.isNotBlank() },
                        body = bodies.filterValues { it.isNotBlank() },
                        imageUrl = imageUrl.trim().ifBlank { null },
                        ctaUrl = ctaUrl.trim().ifBlank { null },
                    ),
                )
            }
            Box(Modifier.width(8.px))
            ActionChip("Cancel", enabled = !busy, onClick = onCancel)
        }
    }
}

// ── Small form primitives (dark theme — SearchInput's visual language) ───

@Composable
private fun FieldLabel(text: String) {
    SpanText(text, Modifier.bodySMedium().color(AppColors.Muted))
}

/** Label on top + input below — placeholders alone read as "where do I type?". */
@Composable
private fun LabeledField(
    label: String,
    value: String,
    width: Int,
    onChange: (String) -> Unit,
) {
    Column {
        FieldLabel(label)
        VSpace(4)
        Input(
            type = InputType.Text,
            attrs = Modifier.toAttrs {
                value(value)
                placeholder("")
                onInput { onChange(it.value) }
                style {
                    property("width", "${width}px")
                    property("max-width", "100%")
                    property("background", INPUT_BG)
                    property("border", INPUT_BORDER)
                    property("border-radius", "10px")
                    property("padding", "9px 12px")
                    property("outline", "none")
                    property("color", "#F0F4FC")
                    property("font-family", "Inter, sans-serif")
                    property("font-size", "13px")
                }
            },
        )
    }
}

@Composable
private fun BodyTextArea(
    value: String,
    onChange: (String) -> Unit,
) {
    TextArea(
        attrs = Modifier.toAttrs {
            value(value)
            placeholder("")
            onInput { onChange(it.value) }
            style {
                property("width", "690px")
                property("max-width", "100%")
                property("min-height", "96px")
                property("background", INPUT_BG)
                property("border", INPUT_BORDER)
                property("border-radius", "10px")
                property("padding", "9px 12px")
                property("outline", "none")
                property("resize", "vertical")
                property("color", "#F0F4FC")
                property("font-family", "Inter, sans-serif")
                property("font-size", "13px")
            }
        },
    )
}

@Composable
private fun ActionChip(
    label: String,
    enabled: Boolean,
    danger: Boolean = false,
    onClick: () -> Unit,
) {
    Box(
        Modifier
            .sBorderRadius(999)
            .padding(leftRight = 16.px, topBottom = 8.px)
            .styleModifier {
                property("background", if (danger) AppColors.Error.toRgbaString(0.12f) else "#101828")
                property(
                    "border",
                    if (danger) "1px solid ${AppColors.Error.toRgbaString(0.30f)}" else "1px solid rgba(255,255,255,0.10)",
                )
                if (!enabled) property("opacity", "0.4")
                property("cursor", if (enabled) "pointer" else "not-allowed")
            }
            .onClick { if (enabled) onClick() },
    ) {
        SpanText(label, Modifier.bodySMedium().color(if (danger) AppColors.Error else AppColors.Ink))
    }
}

@Composable
private fun ToggleChip(
    label: String,
    checked: Boolean,
    onChange: (Boolean) -> Unit,
) {
    Box(
        Modifier
            .sBorderRadius(999)
            .padding(leftRight = 14.px, topBottom = 7.px)
            .styleModifier {
                property("background", if (checked) "#F0F4FC" else INPUT_BG)
                property("border", "1px solid rgba(255,255,255,0.12)")
                property("cursor", "pointer")
            }
            .onClick { onChange(!checked) },
    ) {
        SpanText(label, Modifier.bodySMedium().color(if (checked) AppColors.Surface else AppColors.Ink))
    }
}

@Composable
private fun LangTab(
    label: String,
    selected: Boolean,
    required: Boolean,
    onClick: () -> Unit,
) {
    Box(
        Modifier
            .sBorderRadius(8)
            .padding(leftRight = 10.px, topBottom = 6.px)
            .styleModifier {
                property("background", if (selected) "#F0F4FC" else INPUT_BG)
                property(
                    "border",
                    if (required && !selected) "1px solid rgba(255,255,255,0.25)" else "1px solid rgba(255,255,255,0.10)",
                )
                property("cursor", "pointer")
            }
            .onClick { onClick() },
    ) {
        SpanText(label, Modifier.bodySMedium().color(if (selected) AppColors.Surface else AppColors.Ink))
    }
}
